/**
 * Onboarding tRPC procedures — GSTIN autofill (S03) and Tally migration intent
 * capture (S04). Identity-shaped procedures (`requestOtp`, `verifyOtp`,
 * `createWorkspace`) live in `./identity.ts` because they cross the auth seam.
 *
 * @see prototype/onboarding/gstin.html — S03 baseline
 * @see prototype/onboarding/tally.html — S04 baseline
 */
import { z } from "zod";
import { uuid, gstin, pin, indianState, isoDate } from "../_common.js";

/**
 * GSTIN lookup — pull legal name + tax context from the GST registry so the
 * founder doesn't have to type it. Returns null company shape if the GSTIN is
 * well-formed but not found in the registry; throws for malformed input.
 *
 * The provider is pinned (NIC IRP); see docs/architecture/cookbooks.md for the adapter.
 */
export const lookupGstinInput = z.object({
  gstin,
});

export const lookupGstinOutput = z.object({
  legalName: z.string(),
  tradeName: z.string().nullable(),
  pan: z.string(),
  state: indianState,
  city: z.string(),
  pincode: pin,
  address: z.string(),
  /** Registration type drives which GST schemes apply. */
  type: z.enum(["Regular", "Composition", "SEZ", "Casual"]),
  registeredOn: isoDate.nullable(),
});

/**
 * Record migration intent — append-only on `migration_intents`. Fires the
 * `migration-intent.captured` Inngest event (see ../events) so the migration
 * vertical (V1.x) can pick it up later.
 */
export const recordMigrationIntentInput = z.object({
  source: z.enum(["tally", "zoho", "quickbooks", "none"]),
  /** Captured for audit even when the actual file isn't uploaded yet. */
  fileName: z.string().max(255).optional(),
  /**
   * Tally XML export contents. V1 supports a conservative XML-master import
   * subset (party ledgers + stock items). Binary `.900` backups are not
   * accepted here because the browser cannot inspect them safely.
   */
  fileContent: z.string().max(1_000_000).optional(),
  /** Opening-balance date to use when the XML does not carry one. */
  importAsOf: isoDate.optional(),
  /** True = "do this later" (the demo path). False = "process now". */
  deferred: z.boolean().default(true),
});

export const recordMigrationIntentOutput = z.object({
  migrationIntentId: uuid,
  imported: z
    .object({
      customers: z.number().int().min(0),
      vendors: z.number().int().min(0),
      items: z.number().int().min(0),
    })
    .optional(),
});
